import { parseContent } from '@euler/app/components/message/markdown/MarkdownRenderer';
import {
  AiChatActions,
  AiChatActionsContext,
  AiChatMessageContext,
  AiChatMessageInfo,
} from '@euler/app/flows/ai-suite/chat/AiChatActions';
import { AIAssistantMessage } from '@euler/model/ai';
import { parseBiUploadWidgetPayload } from '@euler/model/bi/widgets';
import { ArrowDown } from 'lucide-react-native';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useTheme } from 'styled-components/native';
import {
  useAIAssistantActions,
  useAIAssistantState,
} from '../AIAssistantProvider';
import { MessageBubble } from './MessageBubble';
import { useStyles } from './styles';

export const MessageList = memo(function MessageList() {
  const state = useAIAssistantState();
  const actions = useAIAssistantActions();
  const styles = useStyles();
  const scrollRef = useRef<FlatList>(null);
  const isAtBottomRef = useRef(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const prevMsgCountRef = useRef(state.messages.length);
  const shouldStickRef = useRef(true);
  const rafRef = useRef<number | null>(null);

  if (state.messages.length !== prevMsgCountRef.current) {
    prevMsgCountRef.current = state.messages.length;
    shouldStickRef.current = true;
  }

  const scheduleScrollToEnd = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      scrollRef.current?.scrollToEnd({ animated: false });
    });
  }, []);

  const handleContentSizeChange = useCallback(() => {
    if (shouldStickRef.current || isAtBottomRef.current) {
      scheduleScrollToEnd();
    }
  }, [scheduleScrollToEnd]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const dist =
        contentSize.height - layoutMeasurement.height - contentOffset.y;
      const atBottom = dist < 80;
      isAtBottomRef.current = atBottom;
      setIsAtBottom(atBottom);
      if (atBottom) shouldStickRef.current = true;
      if (!atBottom && !state.isStreaming) shouldStickRef.current = false;
    },
    [state.isStreaming],
  );

  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    shouldStickRef.current = true;
    scheduleScrollToEnd();
  }, [scheduleScrollToEnd]);

  // The provider resends the turn as it was, a card's tap included.
  const handleRetry = useCallback(() => {
    actions.retry();
  }, [actions]);

  const isLastAssistantError = useMemo(() => {
    if (state.isStreaming) return false;
    const last = state.messages[state.messages.length - 1];
    return (
      last?.role === 'assistant' &&
      !last.isStreaming &&
      (state.error != null || last.content === '抱歉，出现了错误。请稍后再试。')
    );
  }, [state.messages, state.isStreaming, state.error]);

  // What the BI cards may do here, as in the 知车 AI chat. A campaign
  // build's step answer has no place in this panel.
  const { sendMessage, tapSettled$, composerFocus$ } = actions;
  const chatActions = useMemo<AiChatActions>(
    () => ({
      send: (text, extra) =>
        !extra?.answer &&
        sendMessage(text, {
          biAnswer: extra?.biAnswer,
          attachments: extra?.attachments,
        }),
      busy: state.isStreaming || state.isLoading,
      // After the press, so the pressed button does not keep the focus.
      // Only the web composer takes it; the phone's is hold to talk.
      focusComposer: () => {
        setTimeout(() => composerFocus$.next(), 0);
      },
      tapSettled$,
    }),
    [
      sendMessage,
      tapSettled$,
      composerFocus$,
      state.isStreaming,
      state.isLoading,
    ],
  );

  const lastAssistantId = useMemo(() => {
    for (let j = state.messages.length - 1; j >= 0; j--) {
      if (state.messages[j].role === 'assistant')
        return state.messages[j].localId;
    }
    return null;
  }, [state.messages]);

  // Per file upload, the reply holding its newest card: the server writes
  // the card again after each tap, and only the newest acts.
  const uploadsByMessage = useMemo(() => {
    const holders = new Map<string, string>();
    for (const message of state.messages) {
      if (message.role !== 'assistant') continue;
      for (const block of parseContent(message.content)) {
        const ref =
          block.type === 'widget' && block.widget === 'bi_upload'
            ? parseBiUploadWidgetPayload(block.data)
            : null;
        if (ref) holders.set(ref.uploadId, message.localId);
      }
    }
    const byMessage = new Map<string, string[]>();
    for (const [uploadId, localId] of holders)
      byMessage.set(localId, [...(byMessage.get(localId) ?? []), uploadId]);
    return byMessage;
  }, [state.messages]);

  const renderItem = useCallback(
    ({ item, index }: { item: AIAssistantMessage; index: number }) => {
      const isLastMsg = index === state.messages.length - 1;
      return (
        <MessageInfo
          latest={item.localId === lastAssistantId}
          biUploads={uploadsByMessage.get(item.localId) ?? kNoUploads}
        >
          <MessageBubble
            message={item}
            onSuggestionPress={sendMessage}
            isError={isLastMsg && isLastAssistantError}
            onRetry={
              isLastMsg && isLastAssistantError ? handleRetry : undefined
            }
            toolCall={
              isLastMsg && item.isStreaming ? state.currentToolCall : undefined
            }
          />
        </MessageInfo>
      );
    },
    [
      sendMessage,
      state.messages.length,
      state.currentToolCall,
      isLastAssistantError,
      handleRetry,
      lastAssistantId,
      uploadsByMessage,
    ],
  );

  const keyExtractor = useCallback(
    (item: AIAssistantMessage) => item.localId,
    [],
  );

  return (
    <AiChatActionsContext.Provider value={chatActions}>
      <View style={{ flex: 1 }}>
        <FlatList
          ref={scrollRef}
          data={state.messages}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          onScroll={handleScroll}
          onContentSizeChange={handleContentSizeChange}
          onLayout={handleContentSizeChange}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.messageListContent}
          // Scrolling the transcript puts the keyboard away; a tap still reaches
          // the bubbles' own handlers.
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode={
            Platform.OS === 'ios' ? 'interactive' : 'on-drag'
          }
        />
        {!isAtBottom && (
          <TouchableOpacity
            onPress={scrollToBottom}
            style={styles.scrollToBottomBtn}
            activeOpacity={0.8}
          >
            <ArrowDown color="#64748B" size={16} strokeWidth={2.5} />
          </TouchableOpacity>
        )}
        {isLastAssistantError && <ErrorSuggestions />}
      </View>
    </AiChatActionsContext.Provider>
  );
});

const kNoUploads: string[] = [];
const kNoSteps: AiChatMessageInfo['answers'] = [];

/** Which reply a card sits in: only the latest one's questions are open. */
const MessageInfo = memo(function MessageInfo({
  latest,
  biUploads,
  children,
}: {
  latest: boolean;
  biUploads: string[];
  children: React.ReactNode;
}) {
  const info = useMemo<AiChatMessageInfo>(
    () => ({ latest, answers: kNoSteps, biUploads }),
    [latest, biUploads],
  );
  return (
    <AiChatMessageContext.Provider value={info}>
      {children}
    </AiChatMessageContext.Provider>
  );
});

const ErrorSuggestions = memo(function ErrorSuggestions() {
  const theme = useTheme();
  const state = useAIAssistantState();
  const actions = useAIAssistantActions();
  const styles = useStyles();
  const accentColor = theme.link;

  const suggestions =
    state.suggestions.length > 0 ? state.suggestions.map(s => s.text) : [];

  if (suggestions.length === 0) return null;

  return (
    <View style={styles.errorSuggestions}>
      {suggestions.slice(0, 3).map((q, i) => (
        <TouchableOpacity
          key={i}
          activeOpacity={0.7}
          onPress={() => actions.sendMessage(q)}
          style={[
            styles.errorSuggestionChip,
            { borderColor: accentColor + '25' },
          ]}
        >
          <Text
            style={[styles.errorSuggestionText, { color: accentColor }]}
            numberOfLines={1}
          >
            {q}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );
});
